import PropTypes from 'prop-types';
import React from 'react';
import debounce from 'lodash/debounce';
import {connect} from 'react-redux';
import {selectViewportOffsets} from 'state/viewport/viewportSelectors';
import {scrollBarWidth} from 'utilities/userAgent';
import * as FontFaceObserver from 'fontfaceobserver';

const font = new FontFaceObserver('Open Sans', {weight: 300});
let fontLoaded = false;

class FillScale extends React.Component {
  debouncedResize = debounce(() => this.resize(), 150);

  state = {
    width: 0,
    height: 0,
    hasComputed: false,
  };

  componentDidMount() {
    requestAnimationFrame(this.resize);
    window.addEventListener('resize', this.debouncedResize);
    // The web font loads asynchronously and is changing the height of the span of our title
    // and so we need to be notified when it happens and then resize things.
    // see https://stackoverflow.com/questions/5680013/how-to-be-notified-once-a-web-font-has-loaded
    // We are seeing this in the current version of Edge, IE11 and Firefox on Windows
    font.load().then(() => {
      fontLoaded = true;
      this.debouncedResize();
    });
  }

  componentDidUpdate(prevProps) {
    if (prevProps.viewportOffsets != this.props.viewportOffsets) {
      requestAnimationFrame(this.resize);
    }
  }

  componentWillUnmount() {
    window.removeEventListener('resize', this.debouncedResize);
  }

  resize = () => {
    if (!this.el) {
      return;
    }

    const {parentElement, offsetTop, clientWidth} = this.el;

    // DOMO-169798: support when a Domo Everywhere iframe is display: none
    const isHidden = clientWidth === 0;
    if (isHidden) {
      return requestAnimationFrame(this.debouncedResize);
    }

    const {viewportOffsets} = this.props;
    const {innerHeight} = window;
    const parentStyle = window.getComputedStyle(parentElement);
    const paddingBottom = parseInt(
      parentStyle.getPropertyValue('padding-bottom')
    );

    const width = clientWidth - scrollBarWidth;

    const height =
      innerHeight -
      offsetTop -
      paddingBottom -
      viewportOffsets.bottom -
      viewportOffsets.top -
      // When the font loads for the card title, it increases the size of the offsetTop by usually about 8 px.
      // Because this happens before we have a chance to recalculate the height, this causes a temporary vertical
      // scrollbar to appear and then to disappear quickly once we recalculate the height from the new
      // innerHeight. To avoid this appearing and then disappearing of the scrollbar we are going to subtract 10 px
      // from the height until the font has loaded as a buffer so that the scrollbar doesn't appear when the font loads
      // and then we will adjust the height to the true height after the font loads.
      (fontLoaded ? 0 : 10);

    this.setState({
      width,
      height,
      hasComputed: true,
    });
  };

  render() {
    const {children} = this.props;

    return (
      <div ref={ref => (this.el = ref)} style={{height: this.state.height}}>
        {children(this.state.width, this.state.height, this.state.hasComputed)}
      </div>
    );
  }
}

FillScale.propTypes = {
  children: PropTypes.func.isRequired,
};

export default connect(state => ({
  viewportOffsets: selectViewportOffsets(state),
}))(FillScale);
